// The parts every form of the sign-in screens shares: how a send is told (progress, no duplicates, the button
// back after a failure), where a failure of the system is shown (one alert that takes the focus and says how to
// go on), how a wait is told (the real seconds, and the action back when they pass) and the confirmations,
// which only appear after the system confirms. What each message says is in form-messages.ts.
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui';

/** A send in progress: one at a time (a second press or Enter does nothing), and over when it ends, well or not. */
export function useFormAction() {
  const sending = useRef(false);
  const [busy, setBusy] = useState(false);
  const run = useCallback(async <T,>(task: () => Promise<T>): Promise<T | undefined> => {
    // The state only changes after the next render; this changes at once.
    if (sending.current) return undefined;
    sending.current = true;
    setBusy(true);
    try {
      return await task();
    } finally {
      sending.current = false;
      setBusy(false);
    }
  }, []);
  return { busy, run };
}

/** A wait the service asked for: the seconds left (really counted), and a moment to say it is over. */
export function useCooldown() {
  const [until, setUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [over, setOver] = useState(false);
  const remaining = until === null ? 0 : Math.max(0, Math.ceil((until - now) / 1000));

  useEffect(() => {
    if (until === null) return;
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, [until]);

  useEffect(() => {
    if (until !== null && remaining === 0) {
      setUntil(null);
      setOver(true);
    }
  }, [until, remaining]);

  const start = useCallback((seconds: number) => {
    const at = Date.now();
    setNow(at);
    setOver(false);
    setUntil(at + seconds * 1000);
  }, []);
  const clear = useCallback(() => {
    setUntil(null);
    setOver(false);
  }, []);
  return { remaining, active: remaining > 0, over, start, clear };
}

/**
 * A failure of the system, after its answer: one alert that takes the focus (a new attempt that fails again
 * takes it again: `attempt` changes) and, when there is one, the way to go on. The form is left as it was.
 */
export function FormError({
  message,
  attempt,
  children,
}: {
  message: string;
  /** Counts the attempts, so that the same failure twice is announced twice. */
  attempt: number;
  children?: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new attempt is what takes the focus.
  useEffect(() => box.current?.focus(), [attempt]);
  return (
    <div ref={box} role="alert" tabIndex={-1} className="form-error">
      <p className="font-bold">{message}</p>
      {children}
    </div>
  );
}

/**
 * The wait before trying again: the sentence with the real seconds (read once, not every second), and, when
 * they pass, a polite «you can try again» while the action is enabled again.
 */
export function CooldownNotice({
  cooldown,
  seconds,
}: {
  cooldown: ReturnType<typeof useCooldown>;
  seconds: number | null;
}) {
  const { t } = useTranslation();
  if (cooldown.active)
    return (
      <div role="alert" className="form-error">
        <p className="font-bold">
          {t('forms.limit.before')}
          <span className="sr-only">
            {' '}
            {t('forms.limit.seconds', { count: seconds ?? cooldown.remaining })}
          </span>
          <span aria-hidden="true"> {t('forms.limit.seconds', { count: cooldown.remaining })}</span>
        </p>
      </div>
    );
  return (
    <p role="status" className="text-sm font-bold empty:hidden">
      {cooldown.over ? t('forms.limit.over') : ''}
    </p>
  );
}

/** The send button: «Entrar» → «Entrando…», announced; back as it was after a failure; off while a wait lasts. */
export function SubmitButton({
  label,
  busyLabel,
  busy,
  waiting = false,
}: {
  label: string;
  busyLabel: string;
  busy: boolean;
  /** A wait is running: the action is not available until it passes. */
  waiting?: boolean;
}) {
  return (
    <>
      <Button type="submit" isPending={busy} isDisabled={waiting}>
        {busy ? busyLabel : label}
      </Button>
      <span role="status" className="sr-only">
        {busy ? busyLabel : ''}
      </span>
    </>
  );
}
